/**
 * ProgramLandingRedirect
 * 
 * Redirects users to their governance-appropriate landing page when they 
 * access /programs/:programId directly.
 * 
 * CONFIGURATION-DRIVEN ROUTING:
 * - SuperAdmin → /programs/:programId/dashboard
 * - Hospital roles → /programs/:programId/hospital-dashboard
 * - HCP roles → /programs/:programId/hcp-dashboard
 * - Patient roles → /programs/:programId/patient-dashboard
 * - All other operational roles → /programs/:programId/workspace
 * 
 * Landing route resolved from backend `defaultLandingRoute` (MenuKeys.GetDefaultLandingRoute).
 */

import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/modules/shared/contexts/AuthContext';
import { useProgramRoleMenuPermissions } from '../hooks/useProgramRoleMenuPermissions';
import { AuthGuardSkeleton } from '@/modules/shared/components/ui/AppSkeleton';
import { appRoutes, programRoutes } from '@/modules/shared/constants/routes';
import { resolveDashboardLandingPath } from '../dashboard-governance/DashboardLandingResolver';

export function ProgramLandingRedirect() {
  const navigate = useNavigate();
  const { programId } = useParams<{ programId: string }>();
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'SuperAdmin' || user?.role === 'ProductAdmin';

  const {
    isLoading,
    dashboardKey,
    landingRoute,
    defaultLandingRoute,
  } = useProgramRoleMenuPermissions({
    programId,
    enabled: !!programId && !isSuperAdmin,
  });

  useEffect(() => {
    if (!programId) {
      navigate(appRoutes.dashboard(), { replace: true });
      return;
    }

    // SuperAdmin goes to dashboard (they manage system, not operational work)
    if (isSuperAdmin) {
      navigate(programRoutes.dashboard(programId), { replace: true });
      return;
    }

    // Priority 1: Use the user's auth-context landing route (resolved at login time)
    // This avoids waiting for the /my-access API call and is the fastest path.
    if (user?.landingRoute?.trim() && user.programId === programId) {
      console.debug('[ProgramLandingRedirect] Using auth-context landingRoute:', user.landingRoute);
      navigate(user.landingRoute, { replace: true });
      return;
    }

    // Wait for permissions to load
    if (isLoading) return;

    // ─────────────────────────────────────────────────────────────
    // Configuration-driven: use backend-resolved landing route
    // ─────────────────────────────────────────────────────────────
    const redirectPath = resolveDashboardLandingPath({
      programId,
      dashboardKey,
      landingRoute,
      defaultLandingRoute,
      fallbackRoute: programRoutes.workspace(programId),
    });

    console.debug('[ProgramLandingRedirect] Resolved redirect path:', {
      programId,
      dashboardKey,
      landingRoute,
      defaultLandingRoute,
      redirectPath,
    });

    navigate(redirectPath, { replace: true });
  }, [
    programId,
    isSuperAdmin,
    isLoading,
    dashboardKey,
    landingRoute,
    defaultLandingRoute,
    navigate,
    user,
  ]);

  // Show loading state while determining redirect
  return (
    <div className="min-h-screen bg-background px-6 py-4">
      <AuthGuardSkeleton message="Loading program..." className="mx-auto max-w-[1400px]" />
    </div>
  );
}

export default ProgramLandingRedirect;
